import { AssetContext } from '@pages/Asset/Asset.query';
import { useContext, useEffect, useRef, useState } from 'react';

import { ChatTextFormat } from '@pages/Onboarding/components/ChatTextFormat/ChatTextFormat';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { postComments } from '@services/queries/equipments/postComments/postComments';
import {
  shortcuthashes,
  makeShortcutRefCallback,
} from '../Sidebar/components/Shortcuts/Shortcuts.utils';
import {
  Container,
  Title,
  Header,
  Counter,
  ChatWrapper,
  Randomdiv,
} from './AssetHistory.styles';
import { AssetHistoryContent } from './Components/AssetHistoryContent/AssetHistoryContent';
import {
  AssetHistoryLoading,
  CountLoading,
} from './Components/AssetHistoryLoading/AssetHistory.loading';
import { NoAssetHistory } from './Components/NoAssetHistory/NoAssetHistory';

const AssetHistory = () => {
  const {
    assetHistory,
    equipmentQuery,
    shortcutRefs,
    setSentNewComment,
    sentNewComment,
  } = useContext(AssetContext);
  const [text, setText] = useState<string>('');

  const params = useParams<{ equipmentId: string }>();

  const queryClient = useQueryClient();

  const myRef = useRef<HTMLDivElement>(null);

  const [isFullyBottom, setIsFullyBottom] = useState(true);
  const [isFirstRender, setIsFirstRender] = useState(true);

  const showAssetHistory = () => {
    if (assetHistory?.isLoading) {
      return <AssetHistoryLoading />;
    }

    if (assetHistory?.data?.length === 0) {
      return <NoAssetHistory />;
    }

    return <AssetHistoryContent />;
  };

  const count = () => {
    return assetHistory?.data?.length || 0;
  };

  const isCountLoading = () => {
    return assetHistory?.isLoading;
  };

  const mutationPostComments = useMutation(postComments, {
    onSuccess: () => {
      queryClient.invalidateQueries([
        'equipments',
        params.equipmentId,
        'assetHistory',
      ]);
      setText('');
      setSentNewComment(true);
    },
  });

  const refCallback = makeShortcutRefCallback(
    shortcutRefs,
    shortcuthashes.history,
  );

  const scrollToBottom = () => {
    if (myRef.current) {
      myRef.current.scrollTop = myRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (isFullyBottom || isFirstRender || sentNewComment) {
      scrollToBottom();
      setIsFirstRender(false);
      setSentNewComment(false);
    }
  }, [assetHistory?.data, isFullyBottom, isFirstRender]);

  const handleScroll = () => {
    if (myRef.current) {
      const container = myRef.current;
      setIsFullyBottom(
        container.scrollTop + container.clientHeight === container.scrollHeight,
      );
    }
  };

  return (
    <Container ref={refCallback}>
      <Header>
        <Title>History</Title>
        {isCountLoading() ? <CountLoading /> : <Counter>{count()}</Counter>}
      </Header>

      <Randomdiv ref={myRef} onScroll={handleScroll}>
        {showAssetHistory()}
      </Randomdiv>

      <ChatWrapper>
        <ChatTextFormat
          text={text}
          TextEditorHeight="73px"
          placeholder="Click to add a comment"
          setText={setText}
          onMutateMessage={() => {
            equipmentQuery &&
              text &&
              mutationPostComments.mutate({
                id: equipmentQuery.data?.id || '',
                comment: text,
              });
          }}
          disabled={isCountLoading()}
        />
      </ChatWrapper>
    </Container>
  );
};

export { AssetHistory };
